import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedTag } from "./ThemedTag";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@components/firebase/useFirebase";
import { ITag } from "common247";
import { getTags } from "@/services/firebase/tag-functions";

export interface ITagFilterProps {
  /** Array of currently selected tag IDs */
  selectedTagIds: string[];
  /** Callback fired when selected tag IDs change */
  onSelectedTagsChange: (tagIds: string[]) => void;
  /** Optional pre-fetched available tags pool */
  availableTags?: ITag[];
  /** Custom header title (defaults to "Filter by Tags") */
  title?: string;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
  /** Compact density rendering */
  compact?: boolean;
}

/**
 * Filter control allowing users to multi-select tags to filter list items.
 */
export function TagFilter({
  selectedTagIds = [],
  onSelectedTagsChange,
  availableTags,
  title = "Filter by Tags",
  style,
  compact = false,
}: ITagFilterProps) {
  const services = useFirebase();
  const [internalTags, setInternalTags] = useState<ITag[]>(availableTags || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  // Fetch tags if not supplied
  useEffect(() => {
    if (availableTags && availableTags.length > 0) {
      setInternalTags(availableTags);
      return;
    }

    if (!services) return;
    let isMounted = true;
    getTags(services, {})
      .then((res) => {
        const tagsList = res.tags || res.data?.tags;
        if (isMounted && res.status && tagsList) {
          setInternalTags(tagsList);
        }
      })
      .catch((err) => console.warn("[TagFilter] Failed to fetch tags:", err));

    return () => {
      isMounted = false;
    };
  }, [services, availableTags]);

  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const toggleTag = (tagId: string) => {
    if (selectedSet.has(tagId)) {
      onSelectedTagsChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onSelectedTagsChange([...selectedTagIds, tagId]);
    }
  };

  const handleClearAll = () => {
    onSelectedTagsChange([]);
  };

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return internalTags;
    const q = searchQuery.trim().toLowerCase();
    return internalTags.filter((t) => t.value.toLowerCase().includes(q));
  }, [internalTags, searchQuery]);

  if (internalTags.length === 0 && selectedTagIds.length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, compact && styles.containerCompact, style]}>
      <View style={styles.headerRow}>
        <TouchableOpacity
          onPress={() => setIsExpanded(!isExpanded)}
          style={styles.titleToggle}
          activeOpacity={0.7}
        >
          <Ionicons
            name={isExpanded ? "chevron-down" : "chevron-forward"}
            size={14}
            color={tintColor}
            style={styles.chevron}
          />
          <ThemedText style={styles.titleText}>
            🏷️ {title}
            {selectedTagIds.length > 0 && (
              <ThemedText style={{ color: tintColor, fontWeight: "700" }}>
                {" "}({selectedTagIds.length})
              </ThemedText>
            )}
          </ThemedText>
        </TouchableOpacity>

        {selectedTagIds.length > 0 && (
          <TouchableOpacity onPress={handleClearAll} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <ThemedText style={[styles.clearText, { color: tintColor }]}>
              Clear
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {/* Render active selected tags chips even if collapsed */}
      {selectedTagIds.length > 0 && !isExpanded && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.activeTagsRow}
        >
          {selectedTagIds.map((id) => {
            const found = internalTags.find((t) => t.id === id);
            if (!found) return null;
            return (
              <ThemedTag
                key={id}
                tag={found}
                compact
                canRemove
                onRemove={() => toggleTag(id)}
              />
            );
          })}
        </ScrollView>
      )}

      {isExpanded && (
        <View style={styles.expandedSection}>
          {internalTags.length > 5 && (
            <ThemedInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Filter tags..."
              style={styles.filterInput}
            />
          )}

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipScroll}
          >
            {filteredTags.map((tag) => {
              const isSelected = selectedSet.has(tag.id);
              return (
                <TouchableOpacity
                  key={tag.id}
                  onPress={() => toggleTag(tag.id)}
                  activeOpacity={0.7}
                  style={[
                    styles.tagWrapper,
                    isSelected && [
                      styles.selectedTagWrapper,
                      { borderColor: tintColor, backgroundColor: badgeBg },
                    ],
                  ]}
                >
                  <ThemedTag
                    tag={tag}
                    compact={compact}
                    readOnly
                  />
                  {isSelected && (
                    <Ionicons
                      name="checkmark-circle"
                      size={14}
                      color={tintColor}
                      style={styles.checkIcon}
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
  },
  containerCompact: {
    marginVertical: 2,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 2,
  },
  titleToggle: {
    flexDirection: "row",
    alignItems: "center",
  },
  chevron: {
    marginRight: 4,
  },
  titleText: {
    fontSize: 12,
    fontWeight: "600",
  },
  clearText: {
    fontSize: 11,
    fontWeight: "600",
  },
  activeTagsRow: {
    flexDirection: "row",
    paddingVertical: 4,
  },
  expandedSection: {
    paddingTop: 6,
  },
  filterInput: {
    marginBottom: 6,
    height: 32,
    fontSize: 12,
  },
  chipScroll: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    paddingVertical: 2,
  },
  tagWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "transparent",
    paddingRight: 4,
  },
  selectedTagWrapper: {
    borderWidth: 1,
  },
  checkIcon: {
    marginLeft: -2,
    marginRight: 2,
  },
});

export default TagFilter;

